import { z } from 'zod';
import {
  EFormBuilderFieldType,
  ESignatureSettingsRequiredBy,
  ESignatureSettingsType,
} from '@/types';

const optionSchema = z.object({
  id: z.string(),
  value: z.string().min(1, 'Option value is required'),
  error: z.boolean().optional(),
});

// TEXT
type TipTapNode = {
  type?: string;
  text?: string;
  content?: TipTapNode[];
};

/**
 * Recursively checks whether a TipTap node or any of its children contain non-empty text.
 * A node is considered to have text if:
 * - It has a `type` of `"text"` and its `text` property is a non-empty string (ignoring whitespace), or
 * - It has a `content` array with at least one child node that contains text.
 * @param node - The TipTap node to check. Can be undefined or null.
 * @returns `true` if the node or any descendant node contains non-empty text, otherwise `false`.
 */
export const hasTextContent = (node: TipTapNode): boolean => {
  if (!node) return false;
  if (node.type === 'text' && node.text && node.text.trim().length > 0) {
    return true;
  }
  if (node.content && Array.isArray(node.content)) {
    return node.content.some((child) => hasTextContent(child));
  }
  return false;
};

const textFieldSchema = z.object({
  type: z.literal(EFormBuilderFieldType.TEXT),
  id: z.string(),
  text: z.string().refine((val) => {
    if (!val) return false;
    try {
      const parsed = JSON.parse(val);
      return hasTextContent(parsed);
    } catch {
      return false;
    }
  }, 'Text is required'),
});

// CHECKBOX
const checkboxFieldSchema = z.object({
  type: z.literal(EFormBuilderFieldType.CHECKBOX),
  id: z.string(),
  label: z.string().min(1, 'Label is required'),
  options: z.array(optionSchema).min(1, 'At least one option is required'),
  required: z.boolean().optional(),
});

// SINGLE_LINE_INPUT
const singleLineInputFieldSchema = z.object({
  type: z.literal(EFormBuilderFieldType.SINGLE_LINE_INPUT),
  id: z.string(),
  label: z.string().min(1, 'Label is required'),
  placeholder: z.string().optional(),
  required: z.boolean().optional(),
});

// MULTILINE_INPUT
const multilineInputFieldSchema = z.object({
  type: z.literal(EFormBuilderFieldType.MULTILINE_INPUT),
  id: z.string(),
  label: z.string().min(1, 'Label is required'),
  placeholder: z.string().optional(),
  required: z.boolean().optional(),
});

// SINGLE_CHOICE
const singleChoiceFieldSchema = z.object({
  type: z.literal(EFormBuilderFieldType.SINGLE_CHOICE),
  id: z.string(),
  label: z.string().min(1, 'Label is required'),
  options: z.array(optionSchema).min(1, 'At least one option is required'),
  required: z.boolean().optional(),
});

// MULTIPLE_CHOICE
const multipleChoiceFieldSchema = z.object({
  type: z.literal(EFormBuilderFieldType.MULTIPLE_CHOICE),
  id: z.string(),
  label: z.string().min(1, 'Label is required'),
  options: z.array(optionSchema).min(1, 'At least one option is required'),
  required: z.boolean().optional(),
});

// DROPDOWN
const dropdownFieldSchema = z.object({
  type: z.literal(EFormBuilderFieldType.DROPDOWN),
  id: z.string(),
  label: z.string().min(1, 'Label is required'),
  placeholder: z.string().min(1, 'Placeholder is required'),
  options: z.array(optionSchema).min(1, 'At least one option is required'),
  required: z.boolean().optional(),
});

// DATE
const dateFieldSchema = z.object({
  type: z.literal(EFormBuilderFieldType.DATE),
  id: z.string(),
  label: z.string().min(1, 'Label is required'),
  required: z.boolean().optional(),
});

// FILE
const fileFieldSchema = z.object({
  type: z.literal(EFormBuilderFieldType.FILE),
  id: z.string(),
  label: z.string().min(1, 'Label is required'),
  required: z.boolean().optional(),
});

const fieldSchema = z.union([
  textFieldSchema,
  checkboxFieldSchema,
  singleLineInputFieldSchema,
  multilineInputFieldSchema,
  singleChoiceFieldSchema,
  multipleChoiceFieldSchema,
  dropdownFieldSchema,
  dateFieldSchema,
  fileFieldSchema,
]);

const fieldsSchema = z
  .array(fieldSchema)
  .min(1, 'At least one field is required');

export const formBuilderSchema =
  /**
   * Zod validation schema for form builder with title, signature settings and fields
   */
  z.object({
    title: z.string().min(1, 'Title is required'),
    signatureSettings: z.object({
      type: z.nativeEnum(ESignatureSettingsType),
      requiredBy: z.nativeEnum(ESignatureSettingsRequiredBy),
    }),
    fields: fieldsSchema,
  });

export const visitNoteSchema =
  /**
   * Zod validation schema for visit note template with title, fields and
   * signature settings. Only a provider can sign a visit note, and only typed
   * signatures are offered.
   */
  z.object({
    title: z.string().min(1, 'Title is required'),
    signatureSettings: z.object({
      type: z.union([
        z.literal(ESignatureSettingsType.NO_SIGNATURE),
        z.literal(ESignatureSettingsType.TYPED),
      ]),
      requiredBy: z.literal(ESignatureSettingsRequiredBy.PROVIDER),
    }),
    fields: fieldsSchema,
  });
